import { ButtonSecondary } from '@amelio/ui-library';
import { useRef } from 'react';

import { FILTER_BAR_LAYOUT } from '../calibrationStyles';

import type { SelectedFilters } from '@/hooks/filters/useCompanyAttributesFilters';
import type { Company } from '@/types/directory/Company';

import {
    COMPANY_ATTRIBUTE_KINDS,
    CompanyAttributeFilters,
    type CompanyAttributeFiltersHandle,
} from '@/components/CompanyAttributeFilters/CompanyAttributeFilters';
import { useAmelioTranslations } from '@/hooks';

export interface CalibrationFiltersProps {
    company: Company | undefined;
    /** The page's current filter selection, used only to decide whether the reset button shows —
     * `CompanyAttributeFilters` owns the selection state itself and reports changes upward. */
    filters: SelectedFilters;
    onFiltersChange: (filters: SelectedFilters) => void;
}

const hasActiveFilter = (filters: SelectedFilters): boolean =>
    Object.values(filters).some((values) => values.length > 0);

/**
 * The calibration report's company-attribute filter bar: the shared `CompanyAttributeFilters`
 * dropdown, offering every base family (including age group and tenure, which the report's own
 * `ageGroupIds`/`tenureIds` params need — see `toReportFilters`), plus a reset button shown only
 * once a filter is active.
 */
export const CalibrationFilters = ({
    company,
    filters,
    onFiltersChange,
}: CalibrationFiltersProps) => {
    const { t } = useAmelioTranslations();
    const filtersRef = useRef<CompanyAttributeFiltersHandle>(null);

    return (
        <div className={FILTER_BAR_LAYOUT}>
            <CompanyAttributeFilters
                ref={filtersRef}
                company={company}
                onFiltersChange={onFiltersChange}
                attributes={COMPANY_ATTRIBUTE_KINDS}
            />

            {hasActiveFilter(filters) && (
                <ButtonSecondary
                    size={'sm'}
                    onClick={() => filtersRef.current?.reset()}
                    dataTestId={'calibration-filters-reset'}
                >
                    {t('calibration.filters.reset')}
                </ButtonSecondary>
            )}
        </div>
    );
};
